import { createPortal } from "react-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, LoadingSpinner } from "@/components/ui";
import {
  FileViewerContent,
  type FileViewerProps,
} from "@/components/features/matter/file-repo/FileViewer";
import { FileVersionActions, useFileVersionState } from "./FileVersionActions";
import { fileVersionPreviewQueryOptions } from "@/lib/data/react-query/files/canonical-files";
import { fileOperationMessage } from "@/lib/data/requests/files/canonical-files";

export function VersionedFilePreview({
  fileId,
  viewerProps,
}: {
  fileId: string;
  viewerProps: FileViewerProps;
}) {
  const state = useFileVersionState(fileId);
  const [selectedId, setSelectedId] = useState("");
  const selectedVersionId = selectedId || state.file?.currentVersionId || "";
  const isHistorical =
    !!selectedId && selectedId !== state.file?.currentVersionId;
  const onVersionChange = viewerProps.onVersionChange;
  useEffect(() => {
    onVersionChange?.({ fileId, versionId: isHistorical ? selectedId : null });
  }, [fileId, isHistorical, selectedId, onVersionChange]);
  const selectedVersion = state.versions.find(
    (version) => version.id === selectedVersionId,
  );
  const preview = useQuery(
    fileVersionPreviewQueryOptions(
      fileId,
      isHistorical ? selectedId : "",
      selectedVersion?.contentType,
    ),
  );

  const controls = state.canViewHistory ? (
    <FileVersionActions
      fileId={fileId}
      state={state}
      selectedVersionId={selectedVersionId}
      onSelectVersion={(id) => {
        setSelectedId(id === state.file?.currentVersionId ? "" : id);
      }}
    />
  ) : null;
  const controlsContainer = viewerProps.versionControlsContainer;

  return (
    <div className="flex h-full min-h-0 w-full flex-col">
      <div className="min-h-0 flex-1">
        {isHistorical && preview.isPending ? (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : isHistorical && preview.isError ? (
          <div role="alert" className="space-y-3 p-4">
            <p>
              {fileOperationMessage(
                preview.error,
                "Unable to preview this version.",
              )}
            </p>
            <Button variant="outline" onClick={() => void preview.refetch()}>
              Retry preview
            </Button>
          </div>
        ) : (
          <FileViewerContent
            {...viewerProps}
            key={selectedVersionId}
            {...(isHistorical && preview.data
              ? {
                  file: {
                    fileName: preview.data.fileName,
                    presignedURLKey: "",
                  },
                  presignedUrl: preview.data.url,
                  contentType: preview.data.mimeType,
                  onEdit: undefined,
                  onDocxEdit: undefined,
                  canEditDocx: false,
                  canEditOverlay: false,
                  showEditOverlay: false,
                  handleRemoveFileUpload: undefined,
                }
              : {})}
          />
        )}
      </div>
      {controlsContainer !== undefined ? (
        controlsContainer && createPortal(controls, controlsContainer)
      ) : controls ? (
        <div className="border-border flex shrink-0 items-center border-t px-2 pt-3">
          {controls}
        </div>
      ) : null}
    </div>
  );
}
